import { useState, useCallback } from 'react';
import * as Yup from 'yup';
import { ValidationService, ProjectTypeJson } from '../../../services/ValidationService';
import { unit as unitEnum } from '../../../enum/project';
import { DynamicFormData } from '../../../types/FormTypes';
import { logDebug, LogTag } from '../../../utils/utils';
import { COMMON_PROJECT_PROPERTIES } from '../../../types/ProjectDataStructure';

/**
 * Hook for managing form validation
 * Single responsibility: Validation schema and data validation
 */
export const useFormValidation = () => {
    const [validationSchema, setValidationSchema] = useState<Yup.ObjectSchema<DynamicFormData> | null>(null);
    const [validatedData, _setValidatedData] = useState<DynamicFormData>({});

    const setValidatedData = useCallback((data: DynamicFormData) => {
        _setValidatedData(data);
    }, []);

    const createValidationSchema = useCallback((projectTypeJson: ProjectTypeJson, targetUnit: unitEnum, conversionApplied = false) => {
        const conversionState = {
            conversionApplied,
            originalUnit: unitEnum.SI, // API data is always metric
        };

        const schema = ValidationService.generateValidationSchema(projectTypeJson, targetUnit, conversionState);

        setValidationSchema(schema);
        return schema;
    }, []);

    const validateAndStructureData = useCallback((projectInfoData: Record<string, unknown> | undefined | null, method: string, projectTypeJson: ProjectTypeJson): DynamicFormData => {
        if (projectInfoData) {
            logDebug('FORM_VALIDATION', 'Validating existing data structure', projectInfoData, LogTag.FORM_SETUP);

            // 🔧 CRITICAL FIX: Handle both FLAT and NESTED data structures
            // REASON: Redux now stores FLAT format after transformProjectFromBackend() denesting
            // - NESTED: { asphalt: { roadInfo: {...}, trafficLoad: {...} } }
            // - FLAT:   { roadInfo: {...}, trafficLoad: {...}, method: 'asphalt' }
            //
            // Check if data has [method] key (NESTED) or if data is already FLAT
            const hasMethodKey = projectInfoData.hasOwnProperty(method);

            // ✅ PRESERVE common fields BEFORE validation
            // REASON: ValidationService.validateDynamicData only iterates through projectTypeJson fields
            // It doesn't know about common fields (designerFirmCity, nrProject, notes, etc.)
            const preservedCommonFields: Record<string, unknown> = {};
            COMMON_PROJECT_PROPERTIES.forEach(field => {
                if (projectInfoData.hasOwnProperty(field)) {
                    preservedCommonFields[field] = projectInfoData[field];
                }
            });

            let dataForMethod;

            if (hasMethodKey && typeof projectInfoData[method] === 'object' && projectInfoData[method] !== null) {
                // NESTED structure - extract data from [method] key
                dataForMethod = projectInfoData[method] as Record<string, unknown>;
                logDebug('FORM_VALIDATION', 'Using NESTED structure data', { method, hasMethodKey: true }, LogTag.FORM_SETUP);
            } else {
                // FLAT structure - use data directly
                dataForMethod = projectInfoData;
                logDebug('FORM_VALIDATION', 'Using FLAT structure data', { method, hasMethodKey: false }, LogTag.FORM_SETUP);
            }

            const validatedData = ValidationService.validateDynamicData(dataForMethod, projectTypeJson);

            // ✅ RESTORE common fields AFTER validation
            // This ensures they aren't stripped out during the validation process
            const finalData = {
                ...validatedData,
                ...preservedCommonFields,
            };

            if (projectTypeJson.projectType === 'railways') {
                const materials = dataForMethod.materialTypes;
                const soil = dataForMethod.soilProperties;
                const originalMaterials = materials !== null && typeof materials === 'object' && !Array.isArray(materials) ? (materials as Record<string, unknown>) : {};
                const originalSoil = soil !== null && typeof soil === 'object' && !Array.isArray(soil) ? (soil as Record<string, unknown>) : {};
                // Saved values must survive the generic default merge, including empty CBR drafts.
                return {
                    ...finalData,
                    materialTypes: { ...finalData.materialTypes, ballastType: originalMaterials.ballastType ?? '' },
                    soilProperties: { ...finalData.soilProperties, cbrOfBallast: originalSoil.cbrOfBallast ?? '' },
                } as DynamicFormData;
            }

            return finalData as DynamicFormData;
        } else {
            logDebug('FORM_VALIDATION', `Building initial data structure for method: ${method}`, undefined, LogTag.FORM_SETUP);
            const initialData = ValidationService.buildInitialDataStructure(projectTypeJson);
            return initialData;
        }
    }, []);

    const clearValidation = useCallback(() => {
        setValidationSchema(null);
        setValidatedData({});
    }, [setValidatedData]);

    return {
        validationSchema,
        validatedData,
        createValidationSchema,
        validateAndStructureData,
        setValidatedData,
        clearValidation,
    };
};

export type UseFormValidationReturn = ReturnType<typeof useFormValidation>;
